// pages/ChennaiTaxiMasterPage.jsx
import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { 
  FaStar, FaShieldAlt, FaClock, FaWallet, FaHeadset, 
  FaCar, FaMapMarkerAlt, FaPlane, FaRoad, FaPhone, 
  FaWhatsapp, FaCheckCircle, FaRupeeSign, FaUserTie,
  FaTaxi, FaGift, FaCreditCard, FaMobile, FaSearch,
  FaExchangeAlt
} from "react-icons/fa";
import { MdAirportShuttle } from "react-icons/md";
import { FiMapPin, FiCalendar, FiUsers, FiClock as FiClockIcon, FiX } from "react-icons/fi";
import { BsCurrencyRupee } from "react-icons/bs";
import { RiExchangeFundsLine } from "react-icons/ri";
import { getDistanceBetweenLocations, getUserCurrentLocation, searchLocations } from '../../services/distanceService';

// Location Autocomplete Component
const LocationAutocomplete = ({ value, onChange, placeholder, onCurrentLocation, label }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const wrapperRef = useRef(null);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = async (e) => {
    const inputValue = e.target.value;
    onChange(inputValue);
    
    if (inputValue.length > 2) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setIsLoading(true);
      debounceRef.current = setTimeout(async () => {
        try {
          const results = await searchLocations(inputValue, { signal: controller.signal });
          setSuggestions(results);
          setShowSuggestions(true);
        } catch (error) {
          console.error('Location search error:', error);
        } finally {
          setIsLoading(false);
        }
      }, 250);
    } else {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    onChange(suggestion.displayName || suggestion.description);
    setShowSuggestions(false);
  };

  return (
    <div ref={wrapperRef} className="relative w-full text-black">
      {label && <label className="block text-gray-900 text-xs font-medium mb-1">{label}</label>}
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={() => value.length > 2 && suggestions.length > 0 && setShowSuggestions(true)}
          placeholder={placeholder}
          className="w-full border-none outline-none text-base font-medium bg-transparent text-black placeholder:text-gray-400"
        />
        {value && (
          <button onClick={() => onChange('')} className="text-gray-400 hover:text-gray-600">
            <FiX size={16} />
          </button>
        )}
        {onCurrentLocation && (
          <button
            onClick={onCurrentLocation}
            className="text-red-500 hover:text-red-600 text-xs font-medium flex items-center gap-1 whitespace-nowrap"
          >
            <FiMapPin /> Current
          </button>
        )}
      </div>
      
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-60 overflow-y-auto">
          {suggestions.map((suggestion, index) => (
            <div
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
              className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
            >
              <div className="font-medium text-sm">{suggestion.displayName || suggestion.description}</div>
              <div className="text-xs text-gray-500 mt-0.5">{suggestion.address || suggestion.structured_formatting?.secondary_text}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ChennaiTaxiMasterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Booking form state
  const [tripType, setTripType] = useState('oneway');
  const [pickup, setPickup] = useState('');
  const [drop, setDrop] = useState('');
  const [departureDate, setDepartureDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [returnDate, setReturnDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [pickupTime, setPickupTime] = useState('10:00');
  const [isCalculating, setIsCalculating] = useState(false);
  
  const [selectedVehicle, setSelectedVehicle] = useState("");
  const [passengers, setPassengers] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const [showBookingForm, setShowBookingForm] = useState(true);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 767);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Get page type from URL
  const getPageType = () => {
    const path = location.pathname;
    if (path.includes("chennai-airport-taxi")) return "airport";
    if (path.includes("chennai-to-pondicherry-taxi")) return "pondicherry";
    if (path.includes("chennai-to-bangalore-taxi")) return "bangalore";
    if (path.includes("local-taxi-chennai")) return "local";
    return "general";
  };

  const pageType = getPageType();

  // Dynamic content
  const getDynamicContent = () => {
    switch(pageType) {
      case "airport":
        return {
          title: "Chennai Airport Taxi | MAA Airport Cabs | 24/7 Service | BookinIndia",
          description: "Book Chennai Airport taxi from MAA International Airport. 24/7 availability, fixed rates, meet & greet service. Safe airport transfers with flight tracking. Pre-book now and get ₹500 OFF!",
          h1: "Chennai Airport Taxi Service",
          subheading: "Fast & Reliable Transfers from MAA Airport",
          specialOffer: "Airport Special: Fixed rates from ₹450 only!",
          keywords: "chennai airport taxi, maa airport taxi, chennai airport cab, airport transfer chennai",
          defaultPickup: "Chennai Airport (MAA)",
          defaultDrop: "Chennai City"
        };
      case "pondicherry":
        return {
          title: "Chennai to Pondicherry Taxi | Best Rates | One Way Cabs | BookinIndia",
          description: "Book Chennai to Pondicherry taxi at best prices. ₹1,800 onwards for Sedan. One way & round trip cabs available. 160 km scenic drive via ECR with professional drivers.",
          h1: "Chennai to Pondicherry Taxi",
          subheading: "Scenic Coastal Journey at Best Prices",
          specialOffer: "Pondicherry Special: One-way from ₹1,800 only!",
          keywords: "chennai to pondicherry taxi, chennai to pondicherry cab",
          defaultPickup: "Chennai",
          defaultDrop: "Pondicherry"
        };
      case "bangalore":
        return {
          title: "Chennai to Bangalore Taxi | One Way & Round Trip Cabs | BookinIndia",
          description: "Book Chennai to Bangalore taxi with affordable rates. 350 km journey, 5-6 hours travel. One way cabs available with professional drivers.",
          h1: "Chennai to Bangalore Taxi",
          subheading: "Comfortable Intercity Travel",
          specialOffer: "Bangalore Special: One-way from ₹4,500 only!",
          keywords: "chennai to bangalore taxi, chennai to bangalore cab",
          defaultPickup: "Chennai",
          defaultDrop: "Bangalore"
        };
      default:
        return {
          title: "#1 Taxi Service in Chennai | Best Cab Booking Online | BookinIndia",
          description: "BookinIndia offers premium taxi service in Chennai. 24/7 cab booking, airport transfers, outstation trips to Pondicherry & Bangalore, local rides. Safe, reliable, and affordable cabs.",
          h1: "Best Taxi Service in Chennai",
          subheading: "Book Premium Cabs Online with ₹500 OFF",
          specialOffer: "New User Offer: Get ₹500 OFF on first booking!",
          keywords: "taxi service chennai, cab booking chennai, chennai taxi",
          defaultPickup: "",
          defaultDrop: ""
        };
    }
  };

  const content = getDynamicContent();

  // Set default values based on page type
  useEffect(() => {
    if (content.defaultPickup && !pickup) {
      setPickup(content.defaultPickup);
    }
    if (content.defaultDrop && !drop) {
      setDrop(content.defaultDrop);
    }
  }, [content.defaultPickup, content.defaultDrop]);

  const getTodayIso = () => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const minBookingDate = getTodayIso();

  // Format date for display
  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const day = d.getDate();
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return {
      day,
      month: months[d.getMonth()],
      year: `'${String(d.getFullYear()).slice(2)}`,
      dayName: days[d.getDay()]
    };
  };

  const dep = formatDate(departureDate);

  // Get current location
  const handleGetCurrentLocation = async (field) => {
    try {
      const locationData = await getUserCurrentLocation();
      const locationStr = locationData.shortName || locationData.displayName;
      
      if (field === 'pickup') {
        setPickup(locationStr);
      } else if (field === 'drop') {
        setDrop(locationStr);
      }
    } catch (error) {
      console.error('Error getting location:', error);
      alert('Could not get your location. Please check location permissions.');
    }
  };

  // Swap locations
  const handleSwapLocations = () => {
    const temp = pickup;
    setPickup(drop);
    setDrop(temp);
  };

  // Handle search/booking
  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!pickup || !drop) {
      alert('Please enter both pickup and drop locations');
      return;
    }

    setIsCalculating(true);
    
    try {
      // Calculate distance between locations
      const distance = await getDistanceBetweenLocations(pickup, drop);
      
      if (distance) {
        navigate('/vehicle-selection', {
          state: {
            from: pickup,
            to: drop,
            distance: distance.distance,
            duration: distance.duration,
            fromDetails: distance.fromDetails,
            toDetails: distance.toDetails,
            tripType: tripType,
            pickupDate: departureDate,
            returnDate: tripType === 'roundtrip' ? returnDate : null,
            pickupTime: pickupTime,
            service: tripType === 'oneway' ? 'One Way Trip' : 
                     tripType === 'roundtrip' ? 'Round Trip' : 'Airport Transfer',
            mobileNumber: '',
            passengers: passengers,
            selectedVehicle: selectedVehicle
          }
        });
      } else {
        alert('Could not calculate distance. Please check locations or try manual entry.');
      }
    } catch (error) {
      console.error('Search error:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setIsCalculating(false);
    }
  };

  // Schema Markup
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "TravelAgency",
    "name": "BookinIndia",
    "description": content.description,
    "url": "https://www.bookinindia.com",
    "telephone": "+91-XXXXXXXXXX",
    "priceRange": "₹₹",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Chennai",
      "addressRegion": "Tamil Nadu",
      "addressCountry": "IN"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "ratingCount": "3542"
    }
  };

  // Vehicle options
  const vehicles = [
    {
      id: "hatchback",
      name: "Hatchback",
      capacity: "4 Passengers",
      basePrice: "₹8/km",
      hourlyPrice: "₹150/hour",
      airportPrice: "₹450",
      outstationPrice: "₹1,800/day",
      features: ["AC", "Music System", "GPS"],
      icon: "🚗",
      bestFor: "Budget Travel",
      color: "bg-blue-50"
    },
    {
      id: "sedan",
      name: "Sedan",
      capacity: "4 Passengers",
      basePrice: "₹10/km",
      hourlyPrice: "₹180/hour",
      airportPrice: "₹550",
      outstationPrice: "₹2,200/day",
      features: ["AC", "Music System", "GPS", "USB"],
      icon: "🚘",
      bestFor: "Business Travel",
      color: "bg-green-50"
    },
    {
      id: "suv",
      name: "SUV / Innova",
      capacity: "6-7 Passengers",
      basePrice: "₹12/km",
      hourlyPrice: "₹220/hour",
      airportPrice: "₹750",
      outstationPrice: "₹2,800/day",
      features: ["AC", "Music System", "GPS", "Extra Space"],
      icon: "🚙",
      bestFor: "Group Travel",
      color: "bg-yellow-50"
    }
  ];

  // Popular routes
  const popularRoutes = [
    { from: "Chennai Airport (MAA)", to: "Chennai City", price: "₹450 - ₹750", time: "30-60 mins" },
    { from: "Chennai", to: "Pondicherry", price: "₹1,800 - ₹3,500", time: "3 hrs", highlights: "Scenic East Coast Road" },
    { from: "Chennai", to: "Bangalore", price: "₹4,500 - ₹8,000", time: "6-7 hrs" },
    { from: "Chennai", to: "Mahabalipuram", price: "₹1,500 - ₹2,500", time: "1.5 hrs" },
    { from: "Chennai", to: "Tirupati", price: "₹2,500 - ₹4,000", time: "3 hrs" },
    { from: "Chennai", to: "Vellore", price: "₹2,200 - ₹3,800", time: "3 hrs" }
  ];

  // Service areas
  const serviceAreas = [
    "Anna Nagar", "T Nagar", "Adyar", "Velachery", "OMR", "ECR", 
    "Tambaram", "Porur", "Guindy", "Nungambakkam", "Kilpauk", 
    "Mylapore", "Triplicane", "Kodambakkam", "Vadapalani", "Perungudi"
  ];

  // Features
  const features = [
    { icon: <FaShieldAlt className="text-3xl" />, title: "Safe & Secure", description: "GPS tracked, verified drivers, 24/7 support" },
    { icon: <FaClock className="text-3xl" />, title: "24/7 Availability", description: "Book anytime, day or night" },
    { icon: <FaWallet className="text-3xl" />, title: "Best Price", description: "Competitive rates, no hidden charges" },
    { icon: <FaHeadset className="text-3xl" />, title: "24/7 Support", description: "Dedicated customer support team" },
    { icon: <FaCar className="text-3xl" />, title: "Premium Fleet", description: "Well-maintained AC cars" },
    { icon: <FaUserTie className="text-3xl" />, title: "Pro Drivers", description: "Experienced, courteous drivers" }
  ];

  // Testimonials
  const testimonials = [
    {
      name: "Ramesh Kumar",
      location: "Anna Nagar",
      rating: 5,
      review: "Excellent service! Booked Chennai to Pondicherry taxi. Driver was professional, car was clean, reached on time. Highly recommended!",
      trip: "Chennai → Pondicherry",
      vehicle: "Sedan"
    },
    {
      name: "Priya Venkatesh",
      location: "Adyar",
      rating: 5,
      review: "Booked airport taxi for early morning flight. Car arrived 15 mins early, driver helped with luggage. Very reliable!",
      trip: "Adyar → Chennai Airport",
      vehicle: "Hatchback"
    },
    {
      name: "Suresh Reddy",
      location: "Velachery",
      rating: 5,
      review: "Used for local sightseeing. Hourly rental was perfect. Visited 5 places in 8 hours. Great experience!",
      trip: "Local Chennai Tour",
      vehicle: "SUV"
    }
  ];

  // FAQ
  const faqs = [
    {
      q: "How to book a taxi in Chennai online?",
      a: "Booking is simple: 1) Enter pickup and drop locations, 2) Select date and time, 3) Choose your vehicle, 4) Confirm booking. You'll receive instant confirmation with driver details."
    },
    {
      q: "What are the taxi charges in Chennai?",
      a: "Charges vary by vehicle: Hatchback ₹8/km, Sedan ₹10/km, SUV ₹12/km. Hourly rental from ₹150/hour. Airport transfers: ₹450-₹750. Outstation: Chennai to Pondicherry from ₹1,800, Chennai to Bangalore from ₹4,500."
    },
    {
      q: "Do you provide Chennai airport taxi service?",
      a: "Yes, 24/7 Chennai Airport (MAA) taxi service available. Drivers track your flight, offer meet & greet, assist with luggage. Fixed rates with no surge pricing."
    },
    {
      q: "Do you provide one-way taxi from Chennai to Pondicherry?",
      a: "Yes, one-way taxi from Chennai to Pondicherry available from ₹1,800. The 160 km journey takes approximately 3 hours via East Coast Road."
    }
  ];

  return (
    <>
      <Helmet>
        <html lang="en" />
        <title>{content.title}</title>
        <meta name="description" content={content.description} />
        <meta name="keywords" content={content.keywords} />
        <link rel="canonical" href={`https://www.bookinindia.com${location.pathname}`} />
        <meta property="og:title" content={content.title} />
        <meta property="og:description" content={content.description} />
        <meta property="og:type" content="website" />
        <meta name="geo.region" content="IN-TN" />
        <meta name="geo.placename" content="Chennai" />
        <meta name="geo.position" content="13.0827;80.2707" />
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      </Helmet>

      {/* Hero Section with Booking Form */}
      <section className="relative bg-gradient-to-r from-red-600 to-red-800 text-white py-12 md:py-16 -mt-16">
        <div className="absolute inset-0 bg-black opacity-40"></div>
        <div className="relative container mx-auto px-4 z-10">
          <div className="text-center max-w-4xl mx-auto mb-8">
            <h1 className="text-3xl md:text-5xl font-bold mb-4">{content.h1}</h1>
            <p className="text-lg md:text-xl mb-4">{content.subheading}</p>
            <div className="flex flex-wrap justify-center gap-3 mb-6">
              <div className="bg-white/20 px-3 py-1 rounded-full flex items-center gap-1 text-sm">
                <FaStar className="text-yellow-400" /> 4.9/5 (3,500+)
              </div>
              <div className="bg-white/20 px-3 py-1 rounded-full flex items-center gap-1 text-sm">
                <FaClock /> 24/7 Service
              </div>
              <div className="bg-white/20 px-3 py-1 rounded-full flex items-center gap-1 text-sm">
                <FaShieldAlt /> Safe Rides
              </div>
            </div>
            <div className="bg-orange-500 rounded-lg p-3 max-w-2xl mx-auto">
              <p className="font-bold">🎉 {content.specialOffer} Use Code: <span className="text-yellow-300">"CABTRIP"</span> 🎉</p>
            </div>
          </div>

          {/* Booking Form */}
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-5xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 text-center mb-6">
              Book Your Chennai Taxi Now
            </h2>
            
            <form onSubmit={handleSearch} className="space-y-4">
              {/* Trip Type Tabs */}
              <div className="flex gap-2 mb-4 border-b pb-2">
                {[
                  { id: 'oneway', label: 'One Way' },
                  { id: 'roundtrip', label: 'Round Trip' }
                ].map(tab => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setTripType(tab.id)}
                    className={`px-4 py-2 rounded-lg font-medium transition ${
                      tripType === tab.id 
                        ? 'bg-red-600 text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Location Fields */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="relative">
                  <label className="block text-gray-900 text-sm font-medium mb-2 flex items-center gap-2">
                    <FiMapPin className="text-red-600" /> Pickup Location
                  </label>
                  <LocationAutocomplete
                    value={pickup}
                    onChange={setPickup}
                    placeholder="Enter pickup location"
                    onCurrentLocation={() => handleGetCurrentLocation('pickup')}
                  />
                </div>
                <div className="relative">
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-gray-900 text-sm font-medium flex items-center gap-2">
                      <FiMapPin className="text-red-600" /> Drop Location
                    </label>
                    <button
                      type="button"
                      onClick={handleSwapLocations}
                      className="text-red-600 text-sm flex items-center gap-1 hover:text-red-700"
                    >
                      <FaExchangeAlt size={12} /> Swap
                    </button>
                  </div>
                  <LocationAutocomplete
                    value={drop}
                    onChange={setDrop}
                    placeholder="Enter drop location"
                    onCurrentLocation={() => handleGetCurrentLocation('drop')}
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                {/* Date */}
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2 flex items-center gap-2">
                    <FiCalendar /> Departure Date
                  </label>
                  <input
                    type="date"
                    value={departureDate}
                    onChange={(e) => setDepartureDate(e.target.value)}
                    min={minBookingDate}
                    className="w-full border border-gray-300 rounded-lg p-3 text-gray-700 focus:ring-2 focus:ring-red-500"
                  />
                </div>

                {/* Time */}
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2 flex items-center gap-2">
                    <FiClockIcon /> Pickup Time
                  </label>
                  <input
                    type="time"
                    value={pickupTime}
                    onChange={(e) => setPickupTime(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg p-3 text-gray-700 focus:ring-2 focus:ring-red-500"
                  />
                </div>

                {/* Passengers */}
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2 flex items-center gap-2">
                    <FaUserTie /> Passengers
                  </label>
                  <select
                    value={passengers}
                    onChange={(e) => setPassengers(parseInt(e.target.value))}
                    className="w-full border border-gray-300 rounded-lg p-3 text-gray-700"
                  >
                    {[1,2,3,4,5,6,7,8].map(num => (
                      <option key={num} value={num}>{num} Passenger{num > 1 ? 's' : ''}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Vehicle Selection */}
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2 flex items-center gap-2">
                  <FaCar /> Select Vehicle (Optional)
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {vehicles.map(vehicle => (
                    <div
                      key={vehicle.id}
                      onClick={() => setSelectedVehicle(vehicle.id)}
                      className={`border rounded-lg p-3 text-center cursor-pointer transition ${
                        selectedVehicle === vehicle.id 
                          ? 'border-red-600 bg-red-50' 
                          : 'border-gray-200 hover:border-red-300'
                      }`}
                    >
                      <div className="text-2xl mb-1">{vehicle.icon}</div>
                      <div className="text-sm font-medium">{vehicle.name}</div>
                      <div className="text-xs text-gray-500">{vehicle.capacity}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Search Button */}
              <button
                type="submit"
                disabled={isCalculating}
                className="w-full bg-red-600 text-white rounded-lg p-4 font-bold hover:bg-red-700 transition flex items-center justify-center gap-2 disabled:opacity-70"
              >
                {isCalculating ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                    CALCULATING FARE...
                  </>
                ) : (
                  <>
                    <FaSearch /> SEARCH & BOOK NOW →
                  </>
                )}
              </button>
            </form>

            <div className="mt-4 text-center text-gray-500 text-sm">
              <FaMobile className="inline mr-1" /> Available on iOS & Android | <BsCurrencyRupee className="inline mx-1" /> Cash/Card/UPI Accepted
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-10 bg-gray-100">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div><div className="text-3xl font-bold text-red-600">35,000+</div><div className="text-gray-600 text-sm">Happy Customers</div></div>
            <div><div className="text-3xl font-bold text-red-600">50,000+</div><div className="text-gray-600 text-sm">Trips Completed</div></div>
            <div><div className="text-3xl font-bold text-red-600">100+</div><div className="text-gray-600 text-sm">Vehicles Fleet</div></div>
            <div><div className="text-3xl font-bold text-red-600">24/7</div><div className="text-gray-600 text-sm">Support Available</div></div>
          </div>
        </div>
      </section>

      {/* Vehicle Options */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold mb-2">Our Premium Taxi Fleet in Chennai</h2>
            <p className="text-gray-600">Choose from well-maintained AC cars for your travel needs</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {vehicles.map((vehicle, index) => (
              <div key={index} className={`${vehicle.color} border rounded-xl p-6 text-center hover:shadow-xl transition cursor-pointer`}>
                <div className="text-5xl mb-3">{vehicle.icon}</div>
                <h3 className="text-xl font-bold mb-1">{vehicle.name}</h3>
                <p className="text-gray-600 text-sm mb-2">{vehicle.capacity}</p>
                <p className="text-red-600 font-bold mb-2">{vehicle.basePrice}</p>
                <p className="text-green-600 text-xs mb-2">{vehicle.bestFor}</p>
                <div className="flex flex-wrap justify-center gap-1">
                  {vehicle.features.map((feature, i) => (
                    <span key={i} className="bg-white px-2 py-1 rounded text-xs">✓ {feature}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold mb-2">Why Choose BookinIndia?</h2>
            <p className="text-gray-600">Chennai's most trusted taxi service provider</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg text-center">
                <div className="text-red-600 mb-3 flex justify-center">{feature.icon}</div>
                <h3 className="text-lg font-bold mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Routes */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold mb-2">Popular Taxi Routes from Chennai</h2>
            <p className="text-gray-600">Most booked routes with best prices</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {popularRoutes.map((route, index) => (
              <Link 
                key={index}
                to={`/chennai-to-${route.to.toLowerCase().replace(/\s+/g, '-')}-taxi`}
                className="border rounded-lg p-4 hover:shadow-lg transition bg-white group"
                onClick={() => {
                  setPickup(route.from);
                  setDrop(route.to);
                }}
              >
                <div className="flex items-start gap-3">
                  <div className="bg-red-100 p-2 rounded-full">
                    <FaRoad className="text-red-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold group-hover:text-red-600 transition">
                      {route.from} → {route.to}
                    </h3>
                    <p className="text-gray-500 text-xs">{route.time}</p>
                    <p className="text-red-600 font-bold mt-1">{route.price}</p>
                    {route.highlights && (
                      <p className="text-green-600 text-xs mt-1">✨ {route.highlights}</p>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-12 bg-red-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold mb-2">Service Areas in Chennai</h2>
            <p className="text-gray-600">Fast pickup from any location in Chennai</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {serviceAreas.map((area, index) => (
              <div key={index} className="bg-white rounded-lg p-3 text-center shadow hover:shadow-md transition cursor-pointer" onClick={() => setPickup(area)}>
                <p className="font-medium text-sm">{area}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold mb-2">What Our Customers Say</h2>
            <p className="text-gray-600">Trusted by thousands of happy customers</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg border">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <span className="text-red-600 font-bold">{testimonial.name.charAt(0)}</span>
                  </div>
                  <div>
                    <h3 className="font-bold">{testimonial.name}</h3>
                    <p className="text-xs text-gray-500">{testimonial.location}</p>
                  </div>
                  <div className="ml-auto text-yellow-400 flex">
                    {"★".repeat(testimonial.rating)}
                  </div>
                </div>
                <p className="text-gray-700 text-sm mb-3">"{testimonial.review}"</p>
                <div className="flex justify-between text-xs text-gray-500">
                  <span>{testimonial.trip}</span>
                  <span>{testimonial.vehicle}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold mb-2">Frequently Asked Questions</h2>
            <p className="text-gray-600">Everything you need to know about our Chennai taxi service</p>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <details key={index} className="group bg-white rounded-lg p-4 shadow-sm">
                <summary className="font-bold cursor-pointer list-none flex justify-between items-center">
                  {faq.q}
                  <span className="text-red-600 group-open:rotate-180 transition">▼</span>
                </summary>
                <p className="mt-3 text-gray-600 text-sm leading-relaxed">{faq.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-gradient-to-r from-red-600 to-red-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Ready to Book Your Chennai Taxi?</h2>
          <p className="mb-4">Get ₹500 OFF on first booking with code <span className="font-bold text-yellow-300">"CABTRIP"</span></p>
          <div className="flex flex-wrap justify-center gap-3">
            <button 
              onClick={() => document.querySelector('form')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-white text-red-600 px-6 py-2 rounded-full font-bold hover:bg-gray-100 transition"
            >
              Book Now →
            </button>
            <a href="tel:+916309376010" className="bg-transparent border-2 border-white text-white px-6 py-2 rounded-full font-bold hover:bg-white hover:text-red-600 transition flex items-center gap-2">
              <FaPhone /> Call Now
            </a>
            <a href="https://wa.me/916309376010" target="_blank" rel="noopener noreferrer" className="bg-green-500 text-white px-6 py-2 rounded-full font-bold hover:bg-green-600 transition flex items-center gap-2">
              <FaWhatsapp /> WhatsApp
            </a>
          </div>
        </div>
      </section>

      <style>{`
        details summary::-webkit-details-marker { display: none; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .animate-spin { animation: spin 1s linear infinite; }
      `}</style>
    </>
  );
};

export default ChennaiTaxiMasterPage;
